import { h, Fragment } from 'preact';
import { useState, useMemo, useCallback, useEffect } from 'preact/hooks';
import Image from 'utils/image';
import Badge from 'utils/badge';
import CustomizerIcon from 'utils/customizer-icon';
import Swatches from 'utils/swatches';
import Price from '../product/price';

const getCurrentVariant = (saleCollection, product) => {
  if (saleCollection === 'true') {
    return product.variants.find((variant) => variant.saleVariant === 'true');
  }
  return product.variants.find((variant) => variant.saleVariant !== 'true');
};

const ProductCard = ({ product, openCustomizerStyleModal, saleCollection, layout, template, ...props }) => {
  const cardTemplate = template || 'card';
  const isDesktop = layout.isAboveBreakpoint('lg');
  // Detect touch device (mobile)
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Defensive: ensure product.data exists and is an object
  // const safeData = typeof product.data === 'object' && product.data !== null ? product.data : {};
  // Defensive: ensure currentVariant has price for Constructor products
  // Helper to get first valid variant with price > 0
  const getFirstValidVariant = (variants) => variants.find((v) => v.price && v.price.amount > 0);
  const initialVariant = template === 'collection' ? getCurrentVariant(saleCollection, product) : product.variants[0];

  // prices
  let safePrice = initialVariant?.price;
  if (!safePrice || safePrice.amount === 0) {
    const fallbackVariant = getFirstValidVariant(product.variants);
    safePrice = fallbackVariant?.price || { amount: 0, currencyCode: 'USD' };
  }
  if (typeof safePrice === 'number') {
    safePrice = { amount: safePrice, currencyCode: 'USD' };
  } else if (!safePrice && typeof product.price === 'number') {
    safePrice = { amount: product.price, currencyCode: 'USD' };
  } else if (!safePrice && product.price && typeof product.price === 'object') {
    safePrice = product.price;
  }

  const [currentVariant, setCurrentVariant] = useState({ ...initialVariant, price: safePrice });
  const [filteredVariants, setFilteredVariants] = useState(product.variants);

  // Function to get variant images based on the color option
  const getVariantImages = (colorOption) => {
    const variantWithMatchingColor = product.variants.find((v) => {
      const vColorOption = v.selectedOptions?.find((o) => o.name.toLowerCase() === 'color')?.value;
      return vColorOption === colorOption && v.images.length > 0;
    });
    return variantWithMatchingColor?.images || [];
  };

  useEffect(() => {
    if (template === 'collection') {
      const newVariants = filterVariantsBySale(product.variants, saleCollection === 'true');
      setFilteredVariants(newVariants.length > 0 ? newVariants : []);
    }
  }, []);

  // variant images
  const getVariantMediaImages = (variantMediaIds, productMediaIds, productMediaImages) => {
    if (!Array.isArray(variantMediaIds) || !Array.isArray(productMediaIds) || !Array.isArray(productMediaImages)) {
      return [];
    }
    return variantMediaIds
      .map((id) => {
        const idx = productMediaIds.indexOf(id);
        return idx !== -1 ? productMediaImages[idx] : null;
      })
      .filter(Boolean);
  };
  const variantImages = getVariantMediaImages(
    currentVariant?.variant_media_ids,
    product?.media_ids,
    product?.media_images,
  );
  const mainImage = variantImages?.[0]
    ? {
        src: variantImages[0],
        alt: `${product.title} - Main Image`,
      }
    : product?.images?.[0];
  const secondaryImage = variantImages?.[1]
    ? {
        src: variantImages[1],
        alt: `${product.title} - Secondary Image`,
      }
    : product?.images?.[1];

  const filterVariantsBySale = useCallback((variants, isSaleProduct) => {
    const hasSaleVariants = variants?.some((variant) => variant.saleVariant !== false);
    if (!hasSaleVariants && isSaleProduct) return [];
    return isSaleProduct
      ? variants.filter((variant) => variant.saleVariant === 'true')
      : variants.filter((variant) => variant.saleVariant !== 'true');
  }, []);

  if (!currentVariant || filteredVariants.length < 1) return null;

  // badges
  let productBadge = Array.isArray(product.badge) ? product.badge[0] : product.badge;

  if (saleCollection !== 'true' && !props?.collectionHandle?.includes('sale') && currentVariant?.saleProduct) {
    productBadge = 'on-sale';
  }

  if (productBadge === 'new' && props?.collectionHandle?.includes('new')) {
    productBadge = '';
  }

  const badgeData = productBadge
    ? window.domaine.productBadges?.badges.find((badge) => badge.handle === productBadge)
    : null;

  const handleVariantChange = (e, variantId, colorOption = null) => {
    const hoveredVariant = product.variants.find((v) => v.id === variantId);
    const images = getVariantImages(colorOption.value);
    let price = hoveredVariant && hoveredVariant.price;
    if (typeof price === 'number') {
      price = { amount: price, currencyCode: 'USD' };
    } else if (!price || typeof price.amount !== 'number') {
      price = { amount: 0, currencyCode: 'USD' };
    }
    setCurrentVariant({
      ...hoveredVariant,
      price,
      images,
    });
  };

  const swatches = useMemo(
    () => (
      <Swatches
        product={product}
        currentVariant={currentVariant}
        setCurrentVariant={setCurrentVariant}
        handleVariantChange={handleVariantChange}
        condensed={true}
        isDesktop={isDesktop}
        template={cardTemplate}
        filteredVariants={filteredVariants}
      />
    ),
    [isDesktop, filteredVariants, cardTemplate],
  );

  const gan = product.gan || product.gan?.value || product?.master_id?.value || '';
  const isCustomizable =
    (product.isCustomizable || product.tags?.some((tag) => tag === 'is_customizable::True')) &&
    !document.body.classList.contains('loop-returns-activated');
  const isKidsGraphic = product.isKidsGraphic || product.tags?.some((tag) => tag === 'is_kids_graphic::True');
  const isHoodieGraphic = product.isHoodieGraphic || product.tags?.some((tag) => tag === 'is_hoodie_graphic::True');
  const isTankGraphic = product.isTankGraphic || product.tags?.some((tag) => tag === 'is_tank_graphic::True');
  const isDisableCustomizer =
    product.isDisableCustomizer || product.tags?.some((tag) => tag === 'is_disable_customizer::True');

  // TODO
  const customizerBackgroundImageTag = Array.isArray(product.tags)
    ? product.tags.find((tag) => typeof tag === 'string' && tag.startsWith('customizer_background_image::'))
    : undefined;
  const customizerBackgroundImage =
    typeof customizerBackgroundImageTag === 'string'
      ? customizerBackgroundImageTag.split('::')[1]?.trim()
      : product.customizerBackgroundImageUrl?.reference?.image?.url;

  const graphicData = {
    gan,
    title: product.title,
    isKidsGraphic,
    isHoodieGraphic,
    isTankGraphic,
    customizerBackgroundImage,
  };

  const imageClass = 'group relative mb-16 block aspect-square rounded-lg border border-grey-5 no-underline';
  const titleClass = 'body-16-med mb-6 block text-primary lg:pr-40';
  const imageClassCustomizer = `${imageClass} customizer-product-card`;
  const titleClassCustomizer = `${titleClass} customizer-product-card`;

  // Fix handle for Constructor products: remove leading '/products/' if present
  let cleanHandle = product.handle;
  if (typeof cleanHandle === 'string' && cleanHandle.startsWith('/products/')) {
    cleanHandle = cleanHandle.replace(/^\/products\//, '');
  }

  // Get variant id for PDP links
  let pdpVariantId = currentVariant?.id;
  if (typeof pdpVariantId === 'string' && pdpVariantId.includes('/ProductVariant/')) {
    [, pdpVariantId] = pdpVariantId.split('/ProductVariant/');
  }

  // console.log(JSON.stringify(product, null, 2));
  return (
    <article
      className="relative flex w-full flex-col"
      data-cnstrc-item-id={product.id}
      data-cnstrc-item-name={product.title}
      data-cnstrc-item-price={currentVariant?.price?.amount || ''}
      data-cnstrc-item-section={`${props?.isSearch ? 'Products' : ''}`}
    >
      {isCustomizable && !isDisableCustomizer && props.collectionHandle !== 'customizer' && (
        <CustomizerIcon
          classes={'right-[5px] top-[5px]'}
          graphicData={graphicData}
          openCustomizerStyleModal={openCustomizerStyleModal}
        />
      )}
      <a
        aria-label="Product image"
        data-gan={gan}
        className={props.collectionHandle !== 'customizer' ? imageClass : imageClassCustomizer}
        href={
          props.collectionHandle !== 'customizer'
            ? `/products/${cleanHandle}${pdpVariantId ? `?variant=${pdpVariantId}` : ''}${saleCollection === 'true' ? `${pdpVariantId ? '&' : '?'}sale_view=true` : ''}`
            : '#customizer'
        }
        data-customizer-background-img-url={
          props.collectionHandle === 'customizer' ? customizerBackgroundImage : undefined
        }
        data-is-custom-product={props.collectionHandle === 'customizer' ? 'true' : undefined}
        data-graphic-name={props.collectionHandle === 'customizer' ? product.title : undefined}
        data-is-kids-graphic={props.collectionHandle === 'customizer' ? isKidsGraphic : undefined}
        data-is-hoodie-graphic={props.collectionHandle === 'customizer' ? isHoodieGraphic : undefined}
        data-is-tank-graphic={props.collectionHandle === 'customizer' ? isTankGraphic : undefined}
      >
        {badgeData && (
          <Badge
            badge={badgeData}
            classes={'left-[10px] top-[10px]'}
          />
        )}
        {secondaryImage?.src && !isTouchDevice ? (
          <Fragment>
            <span className="flex h-full items-center justify-center">
              <Image
                {...mainImage}
                srcWidths={{ sm: 640 }}
                classes="object-contain h-full group-hover:opacity-0 rounded-lg transition-opacity duration-250"
                alt={mainImage.alt}
              />
            </span>
            <span className="duration-250 absolute inset-0 overflow-hidden opacity-0 transition-opacity group-hover:opacity-100">
              <Image
                {...secondaryImage}
                srcWidths={{ sm: 640 }}
                classes="object-contain h-full rounded-lg"
                alt={secondaryImage.alt}
              />
            </span>
          </Fragment>
        ) : (
          <Fragment>
            <span className="flex h-full items-center justify-center">
              <Image
                {...mainImage}
                srcWidths={{ sm: 640 }}
                classes="object-contain h-full rounded-lg"
                alt={mainImage.alt}
              />
            </span>
          </Fragment>
        )}
      </a>
      {props.collectionHandle !== 'customizer' && swatches}
      <span className="body-14 mb-6 inline-block">{product?.category ? product.category : '\u00A0'}</span>
      <a
        aria-label="Product title"
        data-gan={gan}
        className={props.collectionHandle !== 'customizer' ? titleClass : titleClassCustomizer}
        href={
          props.collectionHandle !== 'customizer'
            ? `/products/${cleanHandle}${pdpVariantId ? `?variant=${pdpVariantId}` : ''}${saleCollection === 'true' ? `${pdpVariantId ? '&' : '?'}sale_view=true` : ''}`
            : '#customizer'
        }
        data-customizer-background-img-url={
          props.collectionHandle === 'customizer' ? customizerBackgroundImage : undefined
        }
        data-is-custom-product={props.collectionHandle === 'customizer' ? 'true' : undefined}
        data-graphic-name={props.collectionHandle === 'customizer' ? product.title : undefined}
        data-is-kids-graphic={props.collectionHandle === 'customizer' ? isKidsGraphic : undefined}
        data-is-hoodie-graphic={props.collectionHandle === 'customizer' ? isHoodieGraphic : undefined}
        data-is-tank-graphic={props.collectionHandle === 'customizer' ? isTankGraphic : undefined}
      >
        {product.title}
      </a>
      {props.collectionHandle !== 'customizer' && (
        <Price
          product={product}
          currentVariant={currentVariant}
        />
      )}
    </article>
  );
};

export default ProductCard;
